/**
 * Build Theme Tree Utility
 * Converts flat theme array into hierarchical tree structure
 * Domain-agnostic utility function
 */

import type { Theme, ThemeNode } from '../domain/models';

/**
 * Enrich themes with all missing parents up to the root
 * This ensures the complete hierarchy can be built even if only child themes are provided
 *
 * @param themes - Flat array of themes (may be incomplete)
 * @param fetchParentTheme - Function to fetch a parent theme by ID (optional)
 * @returns Enriched array of themes with all parents included
 *
 * @example
 * ```ts
 * const themes = [
 *   { id: '2', name: 'Child', parentId: '1' }
 * ];
 * const enriched = enrichThemesWithParents(themes, async (id) => {
 *   return { id: '1', name: 'Parent', parentId: '' };
 * });
 * // Returns: [
 * //   { id: '1', name: 'Parent', parentId: '' },
 * //   { id: '2', name: 'Child', parentId: '1' }
 * // ]
 * ```
 */
export async function enrichThemesWithParents(
  themes: Theme[],
  fetchParentTheme?: (parentId: string) => Promise<Theme | null>
): Promise<Theme[]> {
  if (!fetchParentTheme) {
    // If no fetch function provided, return themes as-is
    return themes;
  }

  const themeMap = new Map<string, Theme>();
  const missingParentIds = new Set<string>();

  // First pass: add all themes to map and identify missing parents
  themes.forEach(theme => {
    themeMap.set(theme.id, theme);

    // If theme has a parent that's not in our map, mark it as missing
    if (theme.parentId && theme.parentId.trim() !== '' && !themeMap.has(theme.parentId)) {
      missingParentIds.add(theme.parentId);
    }
  });

  // Second pass: fetch all missing parents recursively
  const fetchedParents = new Map<string, Theme>();
  const processedIds = new Set<string>();

  async function fetchParentChain(parentId: string): Promise<void> {
    // Early return if fetchParentTheme is not provided (shouldn't happen due to check above, but TypeScript needs this)
    if (!fetchParentTheme) {
      return;
    }

    if (!parentId || parentId.trim() === '' || processedIds.has(parentId)) {
      return;
    }

    processedIds.add(parentId);

    // If parent is already in themeMap, no need to fetch
    if (themeMap.has(parentId)) {
      return;
    }

    // If parent is already fetched, no need to fetch again
    if (fetchedParents.has(parentId)) {
      return;
    }

    try {
      const parent = await fetchParentTheme(parentId);
      if (parent) {
        fetchedParents.set(parent.id, parent);
        themeMap.set(parent.id, parent);

        // Recursively fetch parent's parent if it exists
        if (parent.parentId && parent.parentId.trim() !== '') {
          await fetchParentChain(parent.parentId);
        }
      }
    } catch (error) {
      console.warn(`[enrichThemesWithParents] Failed to fetch parent theme ${parentId}:`, error);
    }
  }

  // Fetch all missing parents
  await Promise.all(Array.from(missingParentIds).map(id => fetchParentChain(id)));

  // Combine original themes with fetched parents
  return Array.from(themeMap.values());
}

/**
 * Build hierarchical theme tree from flat theme array
 * Now handles missing parents by treating themes with missing parents as root themes
 * Use enrichThemesWithParents() first to ensure complete hierarchy
 *
 * @param themes - Flat array of themes with parentId references
 * @param maxDepth - Maximum depth to build (0 = root only, 1 = root + children, etc.). Unlimited if not specified.
 * @returns Hierarchical tree of ThemeNode objects
 *
 * @example
 * ```ts
 * const themes = [
 *   { id: '1', name: 'Parent', color: '#000', type: 'main', parentId: '' },
 *   { id: '2', name: 'Child', color: '#fff', type: 'sub', parentId: '1' },
 *   { id: '3', name: 'Grandchild', color: '#ccc', type: 'sub', parentId: '2' }
 * ];
 *
 * // Build full tree (all levels)
 * const fullTree = buildThemeTree(themes);
 *
 * // Build tree with max 2 levels (root + direct children only)
 * const limitedTree = buildThemeTree(themes, 1);
 * ```
 */
export function buildThemeTree(themes: Theme[], maxDepth?: number): ThemeNode[] {
  const themeMap = new Map<string, ThemeNode>();
  const rootThemes: ThemeNode[] = [];
  const themesWithMissingParents: ThemeNode[] = [];

  // First pass: create map and set up basic structure
  themes.forEach(theme => {
    themeMap.set(theme.id, { ...theme, children: [], level: 0 });
  });

  // Second pass: build hierarchy
  themes.forEach(theme => {
    const themeWithChildren = themeMap.get(theme.id)!;

    if (theme.parentId && theme.parentId.trim() !== '') {
      if (themeMap.has(theme.parentId)) {
        // This theme has a real parent in our data
        const parent = themeMap.get(theme.parentId)!;
        themeWithChildren.level = parent.level + 1;

        // Only add as child if we haven't exceeded maxDepth
        if (maxDepth === undefined || parent.level < maxDepth) {
          parent.children.push(themeWithChildren);
        }
      } else {
        // Parent is missing - treat as root for now, but mark for later
        // This shouldn't happen if enrichThemesWithParents was called first
        console.warn(
          `[buildThemeTree] Theme "${theme.name}" (${theme.id}) has parent "${theme.parentId}" that is not in the theme list. Treating as root theme.`
        );
        themesWithMissingParents.push(themeWithChildren);
      }
    } else {
      // This is a root theme (no parentId or empty parentId)
      rootThemes.push(themeWithChildren);
    }
  });

  // Add themes with missing parents as root themes (fallback behaviour)
  // In a perfect world, enrichThemesWithParents should be called first to prevent this
  themesWithMissingParents.forEach(theme => {
    if (!rootThemes.includes(theme)) {
      rootThemes.push(theme);
    }
  });

  // Sort root themes by weight (Drupal order)
  rootThemes.sort((a, b) => (a.weight || 0) - (b.weight || 0));

  // Sort children of each theme by weight (recursively)
  const sortChildrenRecursively = (node: ThemeNode) => {
    if (node.children && node.children.length > 0) {
      node.children.sort((a, b) => (a.weight || 0) - (b.weight || 0));
      node.children.forEach(child => sortChildrenRecursively(child));
    }
  };

  rootThemes.forEach(theme => sortChildrenRecursively(theme));

  return rootThemes;
}
